/**
 * okrTrackerUnitsService.ts
 *
 * Reads the canonical /units (departments) collection from the shared
 * OKR Tracker Firestore. EPR uses these to populate the per-employee
 * "OKR Dept" picker, which drives the KR filter for the sync.
 *
 * Read-only — EPR never writes to /units. Cached in-memory per session.
 */

import {
  collection, getDocs, query, orderBy,
} from 'firebase/firestore';
import { db } from '../config/firebase';

export interface OkrTrackerUnit {
  id: string;
  name: string;
  code?: string;
  parentId?: string;
  archived?: boolean;
  /** Some divisions are global (e.g. MD, Founders). UI may treat differently. */
  isCrossProject?: boolean;
}

// In-memory cache for this tab. /units is small and HR will hit the
// picker many times in one session.
let cached: OkrTrackerUnit[] | null = null;
let inFlight: Promise<OkrTrackerUnit[]> | null = null;

/**
 * List all OKR Tracker units. Filters out archived by default. Result
 * is memoised for the lifetime of the tab; call `invalidateUnitsCache()`
 * if you need a fresh read.
 */
export async function listUnits(opts: { includeArchived?: boolean } = {}): Promise<OkrTrackerUnit[]> {
  if (cached) {
    return opts.includeArchived
      ? cached
      : cached.filter(u => !u.archived);
  }
  if (inFlight) {
    const all = await inFlight;
    return opts.includeArchived ? all : all.filter(u => !u.archived);
  }

  inFlight = (async () => {
    try {
      // Ordered by name when possible — most OKR Tracker deployments
      // have a name field. Order is best-effort.
      const snap = await getDocs(query(
        collection(db, 'units'),
        orderBy('name'),
      ));
      cached = snap.docs.map(d => {
        const data = d.data() as Omit<OkrTrackerUnit, 'id'>;
        return { id: d.id, ...data };
      });
      return cached;
    } catch {
      // Fall back to unsorted if /units doesn't have a `name` field
      // indexed for ordering.
      try {
        const snap = await getDocs(collection(db, 'units'));
        cached = snap.docs.map(d => {
          const data = d.data() as Omit<OkrTrackerUnit, 'id'>;
          return { id: d.id, ...data };
        });
        return cached;
      } catch (e) {
        cached = null;
        throw e;
      }
    } finally {
      inFlight = null;
    }
  })();

  const all = await inFlight!;
  return opts.includeArchived ? all : all.filter(u => !u.archived);
}

export function invalidateUnitsCache(): void {
  cached = null;
  inFlight = null;
}

/**
 * Fuzzy-match a free-text department name (e.g. from MS Graph directory)
 * to one of the OKR Tracker units. Match priority:
 *   1. Exact case-insensitive match on name
 *   2. Exact case-insensitive match on code
 *   3. Substring match (either direction)
 * Returns null when nothing close enough is found.
 */
export function fuzzyMatchUnit(
  units: OkrTrackerUnit[],
  freeText: string,
): OkrTrackerUnit | null {
  const needle = freeText.trim().toLowerCase();
  if (!needle) return null;

  // Path 1 — exact name match
  const exactName = units.find(u => u.name.toLowerCase() === needle);
  if (exactName) return exactName;

  // Path 2 — exact code match
  const exactCode = units.find(u => u.code?.toLowerCase() === needle);
  if (exactCode) return exactCode;

  // Path 3 — substring match either way
  const substring = units.find(u => {
    const n = u.name.toLowerCase();
    return n.includes(needle) || needle.includes(n);
  });
  if (substring) return substring;

  return null;
}
